import React, { useState } from 'react';
import { useTestContext } from './TestContext';
import Deactivate from 'app/components/Deactivate';
import ConfirmationModal from 'app/components/ConfirmationModal';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';
import db from 'services/firebase';
import { doc, getDoc, updateDoc } from '@firebase/firestore';
import DropdownMenu from '../DropdownMenu';
import useAuditContext from 'app/Hooks/useAuditContext';

interface TestHeaderProps {
  onClose: () => void;
}

const TestHeader: React.FC<TestHeaderProps> = ({ onClose }) => {
  const { tests, id, currentUser, policyID } = useTestContext();
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<any | null>(null);
  const [isProcessingTest, setIsProcessingTest] = useState(false);
  const currentTest = tests.find((test) => test.id === id);
  const { currentAuditContext } = useAuditContext();

  const handleOpenTestModal = (test: any) => {
    setSelectedTest(test);
    setIsTestModalOpen(true);
  };

  const handleCloseTestModal = () => {
    setIsTestModalOpen(false);
    setSelectedTest(null);
  };

  const handleDeactivateClick = () => {
    setIsDeactivateOpen(true);
  };

  const handleDeactivateClose = () => {
    setIsDeactivateOpen(false);
  };

  const [isExecuting, setIsExecuting] = useState(false);

  const handleConfirmTestActivateDeactivate = async () => {
    if (!selectedTest || isExecuting) return;
    setIsExecuting(true);
    setIsProcessingTest(true);

    try {
      const tenantId = currentUser?.tenantId;
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }

      const docRef = doc(db, `tenants/${tenantId}/tests/${selectedTest.id}`);
      let updateData;
      
      if (selectedTest.status === 'Deactivated') {
        if (selectedTest.type === 'Document') {
          const newStatus = selectedTest.files?.length > 0 ? 'Pass' : 'Fail';
          updateData = {
            status: newStatus,
            deactivation_reason: '',
          };
        } else {
          updateData = {
            status: 'Fail',
            deactivation_reason: '',
          };
        }
      } else {
        updateData = {
          status: 'Deactivated',
          deactivation_reason: '',
        };
      }

      await updateDoc(docRef, updateData);

      if (policyID) {
        const policyRef = doc(db, `tenants/${tenantId}/policies/${policyID}`);
        const policyDoc = await getDoc(policyRef);
        const policyData = policyDoc.data();

        let newStatus = 'Incomplete';
        if (policyData?.customfile?.length > 0 && policyData?.created_at) {
          newStatus = 'Needs approval';
        }

        await updateDoc(policyRef, {
          status: newStatus,
        });
      }

      handleCloseTestModal();
      showSuccessToast({ 
        message: `${selectedTest.name} ${selectedTest.status === 'Deactivated' ? 'activated' : 'deactivated'} successfully!` 
      });
    } catch (error) {
      showErrorToast({ 
        message: `Failed to ${selectedTest.status === 'Deactivated' ? 'activate' : 'deactivate'} ${selectedTest?.name || 'test'}` 
      });
    } finally {
      setIsProcessingTest(false);
      setIsExecuting(false);
    }
  };

  return (
    <>
      <div
        className='border-bottom border-gray-300'
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          padding: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          paddingLeft: '15px',
          paddingRight: '12px',
          zIndex: 1200, 
        }}
      >
        <div className='d-flex justify-content-between w-100'>
          {currentTest && (
            <h3 className='modal-title mt-2' key={currentTest.id}>
              {currentTest.name}
            </h3>
          )}
          <div className='d-flex align-items-center gap-2'>
            <DropdownMenu
              currentUser={currentUser}
              currentTest={currentTest}
              handleDeactivateClick={handleDeactivateClick}
              handleOpenTestModal={handleOpenTestModal}
              id={id}
              currentAuditContext={currentAuditContext}
            />
            <div
              className='btn btn-icon btn-sm btn-active-light-primary'
              aria-label='Close'
              onClick={onClose}
              style={{
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.backgroundColor = '#fff';
                  const svgElement = e.currentTarget.querySelector('svg');
                  if (svgElement) {
                    svgElement.style.stroke = '#0E9F6E';
                  }
                }
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.backgroundColor = '#fff';
                  const svgElement = e.currentTarget.querySelector('svg');
                  if (svgElement) {
                    svgElement.style.stroke = '#5A5A5A';
                  }
                }
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#5E6274'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-x'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M18 6l-12 12' />
                <path d='M6 6l12 12' />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <Deactivate
        isOpen={isDeactivateOpen}
        onClose={handleDeactivateClose}
        id={id}
        type='test'
        title='Test'
        policyID={policyID}
      />
      <ConfirmationModal
        isModalOpen={isTestModalOpen}
        hideModal={handleCloseTestModal}
        confirmBtnClickHandler={handleConfirmTestActivateDeactivate}
        toDeleteItemTitle={selectedTest?.name}
        isLoading={isProcessingTest}
        type={selectedTest?.status === 'Deactivated' ? 'activate' : 'deactivate'}
        title={`${selectedTest?.status === 'Deactivated' ? 'Activate' : 'Deactivate'} Test`}
        message={`Are you sure you want to ${
          selectedTest?.status === 'Deactivated' ? 'activate' : 'deactivate'
        } ${selectedTest?.name}?`}
        confirmButtonText={` ${
          selectedTest?.status === 'Deactivated' ? 'Activate' : 'Deactivate'
        }`}
      />
    </>
  );
};

export default TestHeader;